<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { Download, Trash2 } from 'lucide-vue-next'
import { useAuthStore } from '@yuksalish/auth'
import { formatApiError } from '@yuksalish/api-client'
import { formatDisplayDateTime } from '@yuksalish/ui'
import { useHrApi } from '../composables/useHrApi'
import {
  EMPLOYEE_DOCUMENTS_CATEGORY,
  MAX_FILE_BYTES,
  formatFileSize,
  readFileAsDataUrl,
} from '../utils/files'
import type { UploadedFile } from '../types/hr'

/**
 * Employee documents section: scanned passports, contracts, certificates, and
 * other personnel paperwork stored in the universal file registry
 * (`POST /files` with `attachable_type = employee`).
 */
const props = defineProps<{
  employeeId: number
}>()

const { t } = useI18n()
const auth = useAuthStore()
const { listFiles, uploadFile, deleteFile, downloadFile } = useHrApi()

const permissions = computed(() => auth.user?.permissions ?? [])
const canUpload = computed(() => permissions.value.includes('files.create'))
const canDeleteAny = computed(() => permissions.value.includes('files.delete'))

const DOCUMENT_CATEGORIES = ['passport', 'contract', 'certificate', 'medical', 'other'] as const

const CATEGORY_KEYS: Record<string, string> = {
  passport: 'hr.employeeFiles.categoryPassport',
  contract: 'hr.employeeFiles.categoryContract',
  certificate: 'hr.employeeFiles.categoryCertificate',
  medical: 'hr.employeeFiles.categoryMedical',
  other: 'hr.employeeFiles.categoryOther',
}

const files = ref<UploadedFile[]>([])
const loading = ref(true)
const error = ref('')
const actionError = ref('')
const actionMessage = ref('')
const uploading = ref(false)
const busyFileId = ref<number | null>(null)

const fileInput = ref<HTMLInputElement | null>(null)
const selectedFile = ref<File | null>(null)
const category = ref<string>('passport')

async function load() {
  loading.value = true
  error.value = ''
  try {
    const result = await listFiles({
      attachable_type: 'employee',
      attachable_id: props.employeeId,
      per_page: 100,
    })
    files.value = result.items
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

onMounted(load)

watch(
  () => props.employeeId,
  () => {
    void load()
  },
)

function categoryLabel(value: string): string {
  const key = CATEGORY_KEYS[value]
  if (!key) return value
  const label = t(key)
  return label && !String(label).startsWith('hr.') ? label : value
}

function onFileChange(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0] ?? null
  actionError.value = ''
  actionMessage.value = ''

  if (file && file.size > MAX_FILE_BYTES) {
    actionError.value = t('hr.employeeFiles.tooLarge')
    selectedFile.value = null
    input.value = ''
    return
  }

  selectedFile.value = file
}

function clearSelection() {
  selectedFile.value = null
  if (fileInput.value) fileInput.value.value = ''
}

async function submitUpload() {
  actionError.value = ''
  actionMessage.value = ''

  if (!selectedFile.value) {
    actionError.value = t('hr.employeeFiles.noFile')
    return
  }

  uploading.value = true
  try {
    const dataUrl = await readFileAsDataUrl(selectedFile.value)
    await uploadFile({
      file_base64: dataUrl,
      file_name: selectedFile.value.name,
      mime_type: selectedFile.value.type || undefined,
      category: `${EMPLOYEE_DOCUMENTS_CATEGORY}-${category.value}`,
      is_public: 0,
      attachable_type: 'employee',
      attachable_id: props.employeeId,
    })
    actionMessage.value = t('hr.employeeFiles.uploaded')
    clearSelection()
    await load()
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    uploading.value = false
  }
}

function canRemove(file: UploadedFile): boolean {
  if (canDeleteAny.value) return true
  return file.uploaded_by !== null && String(file.uploaded_by) === String(auth.user?.id ?? '')
}

async function remove(file: UploadedFile) {
  actionError.value = ''
  actionMessage.value = ''
  if (!window.confirm(t('hr.employeeFiles.deleteConfirm', { name: file.original_name }))) return

  busyFileId.value = file.id
  try {
    await deleteFile(file.id)
    actionMessage.value = t('hr.employeeFiles.deleted')
    await load()
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    busyFileId.value = null
  }
}

async function download(file: UploadedFile) {
  actionError.value = ''
  busyFileId.value = file.id
  try {
    await downloadFile(file.url, file.original_name)
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    busyFileId.value = null
  }
}
</script>

<template>
  <section class="panel">
    <div class="panel-header">
      <h3>{{ t('hr.employeeFiles.title') }}</h3>
      <span class="empty-inline">{{ t('hr.employeeFiles.count', { count: files.length }) }}</span>
    </div>
    <p class="empty-inline">{{ t('hr.employeeFiles.description') }}</p>

    <form v-if="canUpload" class="form-grid tight" style="margin-top: 12px" @submit.prevent="submitUpload">
      <div class="field">
        <label for="employee-file-category">{{ t('hr.employeeFiles.category') }}</label>
        <select id="employee-file-category" v-model="category">
          <option v-for="option in DOCUMENT_CATEGORIES" :key="option" :value="option">
            {{ categoryLabel(option) }}
          </option>
        </select>
      </div>
      <div class="field">
        <label for="employee-file-input">{{ t('hr.employeeFiles.file') }}</label>
        <input
          id="employee-file-input"
          ref="fileInput"
          type="file"
          accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip"
          @change="onFileChange"
        >
        <small>{{ t('hr.employeeFiles.uploadHint') }}</small>
      </div>
      <div class="field">
        <label>&nbsp;</label>
        <button class="btn-primary" type="submit" :disabled="uploading || !selectedFile">
          {{ uploading ? t('hr.employeeFiles.uploading') : t('hr.employeeFiles.upload') }}
        </button>
      </div>
    </form>

    <p v-if="actionMessage" class="action-message" style="margin-top: 10px">{{ actionMessage }}</p>
    <p v-if="actionError" class="error-inline" style="margin-top: 10px">{{ actionError }}</p>

    <div v-if="loading" class="state-message"><p>{{ t('hr.employeeFiles.loading') }}</p></div>

    <div v-else-if="error" class="state-message error">
      <p>{{ error }}</p>
      <button class="btn-primary" @click="load">{{ t('hr.common.retry') }}</button>
    </div>

    <div v-else-if="files.length === 0" class="panel" style="margin-top: 12px">
      <span class="empty-inline">{{ t('hr.employeeFiles.empty') }}</span>
    </div>

    <div v-else class="table-scroll" style="margin-top: 12px">
      <table class="data-table">
        <thead>
          <tr>
            <th>{{ t('hr.employeeFiles.name') }}</th>
            <th>{{ t('hr.employeeFiles.category') }}</th>
            <th>{{ t('hr.employeeFiles.size') }}</th>
            <th>{{ t('hr.employeeFiles.uploadedBy') }}</th>
            <th>{{ t('hr.employeeFiles.uploadedAt') }}</th>
            <th class="cell-actions">{{ t('hr.common.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="file in files" :key="file.id">
            <td class="cell-name">{{ file.original_name }}</td>
            <td>{{ categoryLabel(file.category) }}</td>
            <td class="cell-num">{{ formatFileSize(file.file_size) }}</td>
            <td>{{ file.uploaded_by_name || '—' }}</td>
            <td class="cell-date">{{ formatDisplayDateTime(file.created_at) }}</td>
            <td class="cell-actions">
              <button
                class="icon-action"
                type="button"
                :disabled="busyFileId === file.id"
                :title="t('hr.employeeFiles.download')"
                :aria-label="t('hr.employeeFiles.download')"
                @click="download(file)"
              >
                <Download :size="16" aria-hidden="true" />
              </button>
              <button
                v-if="canRemove(file)"
                class="icon-action danger"
                type="button"
                :disabled="busyFileId === file.id"
                :title="t('hr.employeeFiles.delete')"
                :aria-label="t('hr.employeeFiles.delete')"
                @click="remove(file)"
              >
                <Trash2 :size="16" aria-hidden="true" />
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>
